<script lang="ts" setup>
import IconChevronLightLeft from '@/assets/icons/chevron-light-left.svg?component'
import IconChevronLightRight from '@/assets/icons/chevron-light-right.svg?component'
import IconChevron from '@/assets/icons/chevron.svg?component'
import IconInfo from '@/assets/icons/status-info-outline.svg?component'
import Button from '@/components/Button.vue'
import Message from '@/components/Message.vue'
import { usePageOverlay } from '@/lib/pageOverlay'
import type { Component } from 'vue'
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    today?: Date
    initialSelectedDate?: Date
    title?: string
    description?: string
    buttonText?: string
    backLinkText?: string
    backButtonText?: string
    message?: {
      text: string
      Icon: Component
    }
  }>(),
  {
    today: () => new Date(),
    initialSelectedDate: () => new Date(),
    title: 'Choose a new order date',
    description:
      'This will change all of your orders, not just the next one. Your order should arrive within 3 working days of the date you select.',
    buttonText: 'Save changes',
    backLinkText: 'Back to your account',
    backButtonText: 'Cancel',
    message: undefined,
  },
)

const emit = defineEmits<{
  (e: 'change', d: Date): void
  (e: 'confirm', d: Date): void
  (e: 'close'): void
}>()

function getDaysInMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
}

function getMonthStartDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1).getDay()
}

function getMonthEndDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDay()
}

function isSameDate(d1: Date, d2?: Date) {
  if (!d2) return false
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  )
}

const selectedDate = ref(props.initialSelectedDate)
watch(selectedDate, () => {
  if (selectedDate.value) emit('change', selectedDate.value)
})

interface CalendarDay {
  date: number
  isToday: boolean
  isSelected: boolean
  isSelectable: boolean
}

interface CalendarMonth {
  length: number
  name: string
  year: number
  month: number
  isCurrentMonth: boolean
  startDay: number
  endDay: number
  placeholderDaysPre: string[]
  days: CalendarDay[]
  placeholderDaysPost: string[]
  isSelectedMonth: boolean
}

const calendar = computed<CalendarMonth[]>(() => {
  const calendar: CalendarMonth[] = []
  for (let i = -1; i <= 3; i++) {
    const monthDate = new Date(
      props.today.getFullYear(),
      props.today.getMonth() + i,
    )
    const length = getDaysInMonth(monthDate)
    const isCurrentMonth =
      monthDate.getFullYear() === props.today.getFullYear() &&
      monthDate.getMonth() === props.today.getMonth()
    const startDay = getMonthStartDay(monthDate)
    const endDay = getMonthEndDay(monthDate)
    const year = monthDate.getFullYear()
    const month = monthDate.getMonth()
    calendar.push({
      length,
      year,
      name: monthDate.toLocaleString('en-gb', { month: 'long' }),
      month,
      isCurrentMonth,
      startDay,
      endDay,
      placeholderDaysPre: Array(startDay)
        .fill(true)
        .map((_, i) =>
          calendar[calendar.length - 1]
            ? `${calendar[calendar.length - 1].length - (startDay - 1 - i)}`
            : '-',
        ),
      days: Array(length)
        .fill(true)
        .map((_, i) => ({
          date: i + 1,
          isToday: isCurrentMonth && i + 1 === props.today.getDate(),
          isSelectable: !isCurrentMonth || i + 1 > props.today.getDate(),
          isSelected:
            !!selectedDate.value &&
            isSameDate(new Date(year, month, i + 1), selectedDate.value),
        })),
      placeholderDaysPost: Array(6 - endDay)
        .fill(true)
        .map((_, i) => `${i + 1}`),
      isSelectedMonth:
        !!selectedDate.value &&
        selectedDate.value.getFullYear() === year &&
        selectedDate.value.getMonth() === month,
    })
  }
  return calendar
})

const selectedMonthIndex = calendar.value.findIndex(
  (month) => month.isSelectedMonth,
)
const visibleMonthIndex = ref(selectedMonthIndex > 0 ? selectedMonthIndex : 1)
const visibleMonth = computed(() => calendar.value[visibleMonthIndex.value])

const showNextMonth = () => {
  visibleMonthIndex.value = Math.min(
    visibleMonthIndex.value + 1,
    calendar.value.length - 1,
  )
}

const showPrevMonth = () => {
  visibleMonthIndex.value = Math.max(visibleMonthIndex.value - 1, 1)
}

const selectDate = (d: number, month: CalendarMonth) => {
  selectedDate.value = new Date(month.year, month.month, d)
}

const confirm = () => {
  if (selectedDate.value) emit('confirm', selectedDate.value)
}

const { show, hide } = usePageOverlay(
  (e) => {
    if (document.querySelector('.PageOverlay') === e.target) {
      emit('close')
    }
  },
  (e) => {
    if (e.key === 'Escape') {
      emit('close')
    }
  },
)

onBeforeMount(show)
onBeforeUnmount(hide)
</script>

<template>
  <div class="DatePicker">
    <div class="DatePicker__Main">
      <div class="DatePicker__Back">
        <Button :modifiers="['link']" @click="$emit('close')">
          <template #iconBefore
            ><IconChevron :flip-y="true"></IconChevron
          ></template>
          {{ backLinkText }}
        </Button>
      </div>
      <h4 v-if="title" class="DatePicker__Title">{{ title }}</h4>
      <div class="DatePicker__Description">
        <div class="DatePicker__Description__Icon">
          <IconInfo width="20" height="20" />
        </div>
        <p>{{ description }}</p>
      </div>
      <div class="DatePicker__Calendar">
        <div class="DatePicker__CalendarHeader">
          <button
            v-if="visibleMonthIndex > 1"
            class="DatePicker__MonthSelect DatePicker__MonthSelect--prev"
            @click="showPrevMonth"
          >
            <IconChevronLightLeft />
          </button>
          <span class="DatePicker__MonthName">{{ visibleMonth.name }}</span>
          <button
            v-if="visibleMonthIndex < calendar.length - 1"
            class="DatePicker__MonthSelect DatePicker__MonthSelect--next"
            @click="showNextMonth"
          >
            <IconChevronLightRight />
          </button>
        </div>
        <div class="DatePicker__Grid">
          <span
            v-for="d in ['S', 'M', 'T', 'W', 'T', 'F', 'S']"
            :key="d"
            class="DatePicker__Grid__Cell DatePicker__Grid__Cell--header"
          >
            {{ d }}
          </span>
          <span
            v-for="d in visibleMonth.placeholderDaysPre"
            :key="d"
            class="DatePicker__Grid__Cell DatePicker__Grid__Cell--inactive"
            >{{ d }}</span
          >
          <span
            v-for="day in visibleMonth.days"
            :key="day.date"
            class="DatePicker__Grid__Cell DatePicker__Grid__Cell"
            :class="{
              'DatePicker__Grid__Cell--selected': day.isSelected,
              'DatePicker__Grid__Cell--selectable': day.isSelectable,
              'DatePicker__Grid__Cell--today': day.isToday,
            }"
            @click="day.isSelectable && selectDate(day.date, visibleMonth)"
          >
            {{ day.date }}
          </span>
          <span
            v-for="d in visibleMonth.placeholderDaysPost"
            :key="d"
            class="DatePicker__Grid__Cell DatePicker__Grid__Cell--inactive"
            >{{ d }}</span
          >
        </div>
      </div>

      <Message
        v-if="
          message &&
          selectedDate &&
          !isSameDate(selectedDate, initialSelectedDate)
        "
        mode="dark"
      >
        <template #icon><component :is="message.Icon" /></template>
        {{ message.text }}
      </Message>

      <div class="Stack Stack--tiny DatePicker__Buttons">
        <Button
          :modifiers="['block']"
          :disabled="
            !selectedDate || isSameDate(selectedDate, initialSelectedDate)
          "
          @click="confirm"
        >
          {{ buttonText }}
        </Button>
        <Button
          :modifiers="['block', 'inverted']"
          :disabled="
            !selectedDate || isSameDate(selectedDate, initialSelectedDate)
          "
          @click="emit('close')"
        >
          {{ backButtonText }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style>
.DatePicker__Back {
  button {
    margin-bottom: 12px;
    color: var(--color-forest-100);
    font-family: var(--font-family-rund);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 150% */
  }
}

.DatePicker__Description {
  border-radius: 4px;
  background: var(--Lichen-100, #e4e8e7);
  display: flex;
  padding: 8px 12px 8px 8px;
  align-self: stretch;
  margin-bottom: 24px;
  gap: 10px;

  svg {
    transform: translateY(2px);
  }

  p {
    color: var(--color-forest-100);
    font-family: var(--font-family-rund);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 150% */
    margin: 0;
  }
}

.DatePicker__Description__Icon {
  margin-top: 5px;
  margin-left: 3px;
}

.DatePicker__Main {
  background-color: var(--color-lichen-25);
  padding: 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;

  @media (--mq-tablet-and-up) {
    background-color: var(--color-white);
  }
}

.DatePicker__Title {
  margin: 0 0 16px;
  min-height: 56px;
  color: var(--color-forest-100);
  font-family: var(--font-family-domaine);
  font-size: 32px;
  font-style: normal;
  font-weight: 400;
  line-height: 40px; /* 125% */

  @media (--mq-tablet-and-up) {
    padding-right: 91px;
  }
}

.DatePicker__Buttons {
  display: flex;
  gap: 12px;
  flex-direction: column;

  @media (--mq-tablet-and-up) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  button {
    margin: 0;
  }
}

.DatePicker__Calendar {
  padding: 16px;
  border-radius: var(--radius-large);
  margin-bottom: 24px;
  border: 1px solid var(--color-lichen-100);
  box-shadow: 0 1px 4px 0 rgba(31, 59, 53, 10%);
  background-color: var(--color-white);
}

.DatePicker__CalendarHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  position: relative;
  height: 36px;
}

.DatePicker__MonthName {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--color-forest-100);
  text-align: center;
  font-family: var(--font-family-rund);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
}

.DatePicker__MonthSelect {
  background: transparent;
  border: 0;
  cursor: pointer;
  width: 36px;
  height: 36px;
  color: var(--color-forest-100);
  border-radius: var(--radius-small);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all var(--global-transition);
  position: absolute;
  top: 0;

  &:hover {
    background-color: transparent !important;
  }

  & svg {
    width: 20px;
    height: 20px;
  }
}

.DatePicker__MonthSelect--prev {
  left: 0;
}

.DatePicker__MonthSelect--next {
  right: 0;
}

.DatePicker__Grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  place-items: center;
  gap: 8px 0;
}

.DatePicker__Grid__Cell {
  color: var(--color-forest-50);
  border-radius: var(--radius-small);
  transition:
    background-color var(--global-transition),
    all var(--global-transition);
  min-height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;

  @media (--mq-tablet-and-up) {
    width: 45px;
    height: 45px;
  }

  &:nth-child(-n + 7) {
    margin: 0 -8px;
  }

  &:nth-child(1) {
    margin-left: 0;
  }

  &:nth-child(7) {
    margin-right: 0;
  }
}

.DatePicker__Grid__Cell--selectable {
  color: var(--color-forest-100);
}

.DatePicker__Grid__Cell--selectable:not(.DatePicker__Grid__Cell--selected) {
  &:hover {
    background-color: var(--color-lichen-25);
    cursor: pointer;
  }
}

.DatePicker__Grid__Cell--today:not(.DatePicker__Grid__Cell--selected) {
  border-radius: 4px;
  border: 1.5px solid var(--color-forest-100);
  color: var(--color-forest-100);
  background: var(--color-white);
}

.DatePicker__Grid__Cell--selected {
  background-color: var(--color-forest-100) !important;
  color: var(--color-warm-white-100);
}

.DatePicker__Grid__Cell--header {
  width: 100%;
  border-bottom: 1px solid var(--color-lichen-150);
  color: var(--color-forest-100);
  border-radius: 0;
}

.DatePicker__Close {
  display: none;
}

.DatePicker .Message {
  margin-bottom: 24px;
}

@media (--mq-tablet-and-up) {
  .DatePicker {
    position: relative;
    box-shadow: 2px 2px 20px 0 rgba(0, 0, 0, 20%);
  }

  .DatePicker__Main::before {
    display: none;
    content: '';
  }

  .DatePicker__Close {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 10px;
    right: 10px;
    border: 0;
    width: 40px;
    height: 40px;
    border-radius: 28px;
    transition: background-color var(--global-transition);
    z-index: 1;
    color: var(--color-forest-100);
    background-color: transparent;

    & svg {
      width: 40px;
      height: 40px;
    }

    &:hover {
      cursor: pointer;
    }
  }

  .DatePicker__Title {
    padding-right: 0;
    max-width: 380px;
    min-height: 0;
  }
}
</style>
